// theme-sources/js/core-ui/components/quick-add-modal.js
import ModalDialog from "./modal-dialog";
import { sectionLogger } from "./_logger";
import { THEME_EVENTS, subscribe } from "../../shared/events";

const DEBUG = true;
const logger = sectionLogger("QuickAddModal", DEBUG);

class QuickAddModal extends ModalDialog {
  constructor() {
    super();
    this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

    this.addEventListener("product-info:loaded", ({ target }) => {
      target.addPreProcessCallback(this.preprocessHTML.bind(this));
    });
  }

  connectedCallback() {
    // Pour éviter d'écraser le lifecycle parent
    if (super.connectedCallback) super.connectedCallback();
    this.unsubscribeCart = subscribe(THEME_EVENTS.cartUpdate, ({ source }) => {
      // On ferme uniquement si ça vient d’un product-form
      if (source === "product-form") {
        this.hide();
      }
    });
  }

  disconnectedCallback() {
    if (this.unsubscribeCart) {
      this.unsubscribeCart();
    }
  }

  hide(preventFocus = false) {
    //const cartNotification = document.querySelector("cart-drawer");
    //if (cartNotification) cartNotification.setActiveElement(this.openedBy);
    this.modalContent.innerHTML = "";
    //if (preventFocus) this.openedBy = null;
    super.hide();
  }

  show(opener) {
    //console.log("SHOW", opener.getAttribute("data-product-url"));
    opener.setAttribute("aria-disabled", true);
    opener.setAttribute("disabled", "disabled");
    opener.classList.add("loading");
    opener.querySelector(".loading__spinner").classList.remove("hidden");

    fetch(opener.getAttribute("data-product-url"))
      .then((response) => response.text())
      .then((responseText) => {
        const responseHTML = new DOMParser().parseFromString(
          responseText,
          "text/html",
        );
        const productElement = responseHTML.querySelector("product-info");

        this.preprocessHTML(productElement);
        HTMLUpdateUtility.setInnerHTML(
          this.modalContent,
          productElement.outerHTML,
        );

        if (window.Shopify && Shopify.PaymentButton) {
          Shopify.PaymentButton.init();
        }
        if (window.ProductModel) window.ProductModel.loadShopifyXR();

        super.show(opener);
      })
      .finally(() => {
        opener.removeAttribute("aria-disabled");
        opener.removeAttribute("disabled");
        opener.classList.remove("loading");
        opener.querySelector(".loading__spinner").classList.add("hidden");
      });
  }

  preprocessHTML(productElement) {
    productElement.classList.forEach((classApplied) => {
      if (classApplied.startsWith("color-") || classApplied === "gradient")
        this.modalContent.classList.add(classApplied);
    });
    this.preventDuplicatedIDs(productElement);
    this.removeDOMElements(productElement);
    //this.removeGalleryListSemantic(productElement);
    //this.updateImageSizes(productElement);
    this.preventVariantURLSwitching(productElement);
  }

  preventVariantURLSwitching(productElement) {
    productElement.setAttribute("data-update-url", "false");
  }

  removeDOMElements(productElement) {
    //console.log("productElement", productElement);

    const pickupAvailability = productElement.querySelector(
      "pickup-availability",
    );
    if (pickupAvailability) pickupAvailability.remove();

    const productModal = productElement.querySelector("product-modal");
    if (productModal) productModal.remove();

    // MCP
    const productGallery = productElement.querySelector(
      ".product-info__medias",
    );
    if (productGallery) productGallery.remove();
    // end MCP

    const modalDialog = productElement.querySelectorAll("modal-dialog");
    if (modalDialog) modalDialog.forEach((modal) => modal.remove());
  }

  preventDuplicatedIDs(productElement) {
    const sectionId = productElement.dataset.sectionId;
    if (!sectionId) return;

    const oldId = sectionId;
    const newId = `quickadd-${sectionId}`;
    productElement.innerHTML = productElement.innerHTML.replaceAll(
      oldId,
      newId,
    );
    Array.from(productElement.attributes).forEach((attribute) => {
      if (attribute.value.includes(oldId)) {
        productElement.setAttribute(
          attribute.name,
          attribute.value.replace(oldId, newId),
        );
      }
    });
    productElement.dataset.originalSection = sectionId;
  }

  removeGalleryListSemantic(productElement) {
    const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
    if (!galleryList) return;

    galleryList.setAttribute("role", "presentation");
    galleryList
      .querySelectorAll('[id^="Slide-"]')
      .forEach((li) => li.setAttribute("role", "presentation"));
  }

  updateImageSizes(productElement) {
    const product = productElement.querySelector(".product");
    const desktopColumns = product?.classList.contains("product--columns");
    if (!desktopColumns) return;

    const mediaImages = product.querySelectorAll(".product__media img");
    if (!mediaImages.length) return;

    let mediaImageSizes =
      "(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)";

    if (product.classList.contains("product--medium")) {
      mediaImageSizes = mediaImageSizes.replace("715px", "605px");
    } else if (product.classList.contains("product--small")) {
      mediaImageSizes = mediaImageSizes.replace("715px", "495px");
    }

    mediaImages.forEach((img) => img.setAttribute("sizes", mediaImageSizes));
  }
}

if (!customElements.get("quick-add-modal")) {
  customElements.define("quick-add-modal", QuickAddModal);
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(
    oldNode,
    newContent,
    preProcessCallbacks = [],
    postProcessCallbacks = [],
  ) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement("div");
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll("[id], [form]").forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form &&
        element.setAttribute(
          "form",
          `${element.form.getAttribute("id")}-${uniqueKey}`,
        );
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = "none";

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll("script").forEach((oldScriptTag) => {
      const newScriptTag = document.createElement("script");
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}
